import { defineComponent as _defineComponent } from 'vue'
import { withModifiers as _withModifiers, createElementVNode as _createElementVNode, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 flex-shrink-0" }
const _hoisted_2 = { class: "relative flex-1 max-w-md ml-auto min-w-0" }
const _hoisted_3 = ["aria-expanded", "onKeydown"]
const _hoisted_4 = {
  key: 1,
  class: "absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg",
  role: "listbox"
}
const _hoisted_5 = {
  key: 0,
  class: "px-3 py-3 text-sm text-gray-500"
}
const _hoisted_6 = ["data-color-search-result", "aria-selected", "onMousedown"]
const _hoisted_7 = { class: "truncate" }
const _hoisted_8 = { class: "flex md:hidden flex-col border-b border-gray-100 flex-shrink-0 overscroll-contain" }
const _hoisted_9 = { class: "collapse-panel__inner" }
const _hoisted_10 = { class: "flex gap-2 px-3 pb-2 overflow-x-auto" }
const _hoisted_11 = ["onClick"]
const _hoisted_12 = { class: "collapse-panel__inner" }
const _hoisted_13 = { class: "flex gap-2 px-3 pb-2 overflow-x-auto" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "flex flex-1 min-h-0 overflow-hidden" }
const _hoisted_16 = { class: "hidden md:block w-44 lg:w-48 flex-shrink-0 bg-gray-50 border-r border-gray-200 overflow-y-auto overscroll-contain" }
const _hoisted_17 = { class: "collapse-panel__inner collapse-panel__inner--sidebar" }
const _hoisted_18 = ["onClick"]
const _hoisted_19 = { class: "truncate flex-1" }
const _hoisted_20 = {
  key: 0,
  class: "text-xs text-gray-500"
}
const _hoisted_21 = { class: "collapse-panel__inner collapse-panel__inner--sidebar" }
const _hoisted_22 = ["onClick"]
const _hoisted_23 = { class: "truncate flex-1" }
const _hoisted_24 = {
  key: 0,
  class: "text-xs text-gray-500"
}
const _hoisted_25 = { class: "flex-1 overflow-y-auto overscroll-contain p-3 sm:p-5" }
const _hoisted_26 = {
  key: 0,
  class: "text-sm font-medium text-black mb-3 capitalize"
}
const _hoisted_27 = {
  key: 1,
  class: "text-sm text-gray-500 py-8 text-center"
}
const _hoisted_28 = {
  key: 2,
  class: "grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-4 sm:gap-x-6 sm:gap-y-5"
}
const _hoisted_29 = ["disabled", "onClick"]
const _hoisted_30 = { class: "truncate" }
const _hoisted_31 = {
  key: 0,
  class: "fas fa-check text-[10px] flex-shrink-0"
}
const _hoisted_32 = {
  key: 0,
  class: "absolute -top-1 -right-1 w-4 h-4 rounded-full bg-black text-white flex items-center justify-center shadow"
}
const _hoisted_33 = { class: "flex items-center justify-end gap-3 px-3 sm:px-4 py-3 border-t border-gray-200 flex-shrink-0" }
const _hoisted_34 = ["onKeydown"]

import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import type { CouleurAPI } from "@/types/mel-components";
import {
  type BasicColorFamily,
  type BasicColorFamilyId,
  getColorSwatchStyle,
  getFamiliesWithVendorColors,
  groupVendorColorsByBasicFamily,
} from "@/utils/colorFamilies";
import {
  type MotifCategory,
  formatMotifPanelTitle,
  getMotifCategoriesWithColors,
  groupMotifsByCategory,
  splitSolidAndMotifColors,
} from "@/utils/motifFamilies";

type PickerSection = "couleurs" | "motifs";


export default /*@__PURE__*/_defineComponent({
  __name: 'CouleursPickerModal',
  props: {
    isVisible: { type: Boolean },
    couleursList: {},
    selectedIds: {},
    officialOnly: { type: Boolean, default: true },
    linkedSourceLabelsByColorId: {},
    single: { type: Boolean },
    disabledIds: {}
  },
  emits: ["update:isVisible", "confirm"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const searchTerm = ref("");
const modalRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);
const confirmButtonRef = ref<HTMLButtonElement | null>(null);
const highlightedColorIndex = ref(-1);
const isSearchDropdownOpen = ref(false);
const activeSection = ref<PickerSection>("couleurs");
const activeFamilyId = ref<BasicColorFamilyId>("beige");
const activeMotifCategory = ref<MotifCategory>("Animaux");
const pendingIds = ref<Set<number>>(new Set());
const couleursSectionOpen = ref(true);
const motifsSectionOpen = ref(false);

const familySwatchStyle = (family: BasicColorFamily) => {
  if (family.swatch.startsWith("linear-gradient")) {
    return { backgroundImage: family.swatch };
  }
  return { backgroundColor: family.swatch };
};

const disableBodyScroll = () => {
  document.documentElement.style.overflow = "hidden";
  document.body.style.overflow = "hidden";
};

const enableBodyScroll = () => {
  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
};

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const matchesSearch = (color: CouleurAPI, term: string) =>
  normalize(color.couleur_FR || "").includes(term) ||
  normalize(color.couleur_EN || "").includes(term) ||
  linkedSourceLabels(color.id_couleur).some((label) =>
    normalize(label).includes(term),
  );

const linkedSourceLabels = (colorId: number | string) => {
  const id = Number(colorId);
  if (!Number.isInteger(id) || id <= 0) return [];
  return props.linkedSourceLabelsByColorId?.[id] ?? [];
};

const availableColors = computed(() => {
  const list = props.couleursList || [];
  if (!props.officialOnly) return list;
  const hasOfficielFlag = list.some(
    (c) => c.officiel !== undefined && c.officiel !== null,
  );
  if (!hasOfficielFlag) return list;
  return list.filter((c) => Number(c.officiel) === 1);
});

const solidColorsRef = computed(() =>
  splitSolidAndMotifColors(availableColors.value).solid,
);
const motifColorsRef = computed(() =>
  splitSolidAndMotifColors(availableColors.value).motifs,
);

const colorGroups = computed(() =>
  groupVendorColorsByBasicFamily(solidColorsRef.value),
);

const motifGroups = computed(() => groupMotifsByCategory(motifColorsRef.value));

const visibleColorFamilies = computed(() => {
  if (searchTerm.value) {
    const term = normalize(searchTerm.value);
    return getFamiliesWithVendorColors(solidColorsRef.value).filter((family) =>
      (colorGroups.value[family.id] || []).some((color) =>
        matchesSearch(color, term),
      ),
    );
  }
  return getFamiliesWithVendorColors(solidColorsRef.value);
});

const visibleMotifCategories = computed(() => {
  if (searchTerm.value) {
    const term = normalize(searchTerm.value);
    return getMotifCategoriesWithColors(motifColorsRef.value).filter(
      (category) =>
        (motifGroups.value[category.id] || []).some((color) =>
          matchesSearch(color, term),
        ),
    );
  }
  return getMotifCategoriesWithColors(motifColorsRef.value);
});

const displayedShades = computed(() => {
  const term = searchTerm.value ? normalize(searchTerm.value) : "";

  if (activeSection.value === "motifs") {
    let list = motifGroups.value[activeMotifCategory.value] || [];
    if (term) list = list.filter((color) => matchesSearch(color, term));
    return list;
  }

  let list = colorGroups.value[activeFamilyId.value] || [];
  if (term) list = list.filter((color) => matchesSearch(color, term));
  return list;
});

const searchResults = computed(() => {
  const term = normalize(searchTerm.value);
  if (!term) return [];
  return availableColors.value.filter(
    (color) =>
      !isPending(color.id_couleur) && matchesSearch(color, term),
  );
});

const panelTitle = computed(() => {
  if (activeSection.value === "motifs") {
    return formatMotifPanelTitle(activeMotifCategory.value);
  }
  const family = visibleColorFamilies.value.find(
    (item) => item.id === activeFamilyId.value,
  );
  return family ? `Nuances de ${family.label.toLowerCase()}` : "";
});

const pendingCountByColorFamily = computed(() => {
  const counts: Partial<Record<BasicColorFamilyId, number>> = {};
  for (const family of visibleColorFamilies.value) {
    const count = (colorGroups.value[family.id] || []).filter((color) =>
      pendingIds.value.has(parseInt(String(color.id_couleur), 10)),
    ).length;
    if (count > 0) counts[family.id] = count;
  }
  return counts;
});

const pendingCountByMotifCategory = computed(() => {
  const counts: Partial<Record<MotifCategory, number>> = {};
  for (const category of visibleMotifCategories.value) {
    const count = (motifGroups.value[category.id] || []).filter((color) =>
      pendingIds.value.has(parseInt(String(color.id_couleur), 10)),
    ).length;
    if (count > 0) counts[category.id] = count;
  }
  return counts;
});

const addButtonLabel = computed(() => {
  const n = pendingIds.value.size;
  if (n === 0) return "Valider";
  if (n === 1) return "Ajouter 1 couleur";
  return `Ajouter ${n} couleurs`;
});

const isPending = (id: number | string) =>
  pendingIds.value.has(parseInt(String(id), 10));

const isMotif = (color: CouleurAPI) => Boolean(color.motifCategorie);

const scrollHighlightedColorIntoView = () => {
  const color = searchResults.value[highlightedColorIndex.value];
  if (!color) return;
  const element = modalRef.value?.querySelector<HTMLElement>(
    `[data-color-search-result="${color.id_couleur}"]`,
  );
  element?.scrollIntoView({ block: "nearest" });
};

const moveColorHighlight = (delta: number) => {
  isSearchDropdownOpen.value = true;
  const length = searchResults.value.length;
  if (!length) return;
  if (highlightedColorIndex.value < 0) {
    highlightedColorIndex.value = delta > 0 ? 0 : length - 1;
  } else {
    highlightedColorIndex.value =
      (highlightedColorIndex.value + delta + length) % length;
  }
  void nextTick(scrollHighlightedColorIntoView);
};

const selectHighlightedColor = () => {
  const color =
    searchResults.value[
      highlightedColorIndex.value >= 0 ? highlightedColorIndex.value : 0
    ];
  if (color) selectSearchResult(color);
};

const showColorPage = (color: CouleurAPI) => {
  const colorId = Number(color.id_couleur);
  if (isMotif(color)) {
    const category = visibleMotifCategories.value.find((item) =>
      (motifGroups.value[item.id] || []).some(
        (candidate) => Number(candidate.id_couleur) === colorId,
      ),
    );
    if (category) selectMotifCategory(category.id);
    return;
  }

  const family = visibleColorFamilies.value.find((item) =>
    (colorGroups.value[item.id] || []).some(
      (candidate) => Number(candidate.id_couleur) === colorId,
    ),
  );
  if (family) selectColorFamily(family.id);
};

const selectSearchResult = (color: CouleurAPI) => {
  showColorPage(color);
  toggleColor(color);
  searchTerm.value = "";
  isSearchDropdownOpen.value = false;
  highlightedColorIndex.value = -1;
  void nextTick(() => searchInputRef.value?.focus());
};

const clearSearch = () => {
  searchTerm.value = "";
  isSearchDropdownOpen.value = false;
  highlightedColorIndex.value = -1;
  void nextTick(() => searchInputRef.value?.focus());
};

const openSearchDropdown = (event?: Event) => {
  const fieldValue =
    event?.target instanceof HTMLInputElement
      ? event.target.value
      : searchTerm.value;
  isSearchDropdownOpen.value = Boolean(fieldValue.trim());
};

const closeSearchDropdown = () => {
  isSearchDropdownOpen.value = false;
};

const focusConfirmButton = () => {
  closeSearchDropdown();
  confirmButtonRef.value?.focus();
};

const focusSearchInput = () => {
  searchInputRef.value?.focus();
};

const isActiveNav = (section: PickerSection, id: string) =>
  activeSection.value === section &&
  (section === "couleurs"
    ? activeFamilyId.value === id
    : activeMotifCategory.value === id);

const navButtonClass = (section: PickerSection, id: string) =>
  isActiveNav(section, id)
    ? "bg-black text-white border-black"
    : "bg-white text-black border-gray-300";

const sidebarItemClass = (section: PickerSection, id: string) =>
  isActiveNav(section, id)
    ? "bg-white text-black font-medium shadow-sm"
    : "text-gray-700 hover:bg-white/70";

const selectColorFamily = (familyId: BasicColorFamilyId) => {
  activeSection.value = "couleurs";
  activeFamilyId.value = familyId;
  couleursSectionOpen.value = true;
  motifsSectionOpen.value = false;
};

const selectMotifCategory = (category: MotifCategory) => {
  activeSection.value = "motifs";
  activeMotifCategory.value = category;
  motifsSectionOpen.value = true;
  couleursSectionOpen.value = false;
};

const toggleCouleursSection = () => {
  const willOpen = !couleursSectionOpen.value;
  couleursSectionOpen.value = willOpen;
  if (willOpen) motifsSectionOpen.value = false;
};

const toggleMotifsSection = () => {
  const willOpen = !motifsSectionOpen.value;
  motifsSectionOpen.value = willOpen;
  if (willOpen) couleursSectionOpen.value = false;
};

const isColorDisabled = (colorId: number | string) => {
  const id = parseInt(String(colorId), 10);
  return (props.disabledIds ?? []).some((item) => Number(item) === id);
};

const toggleColor = (color: CouleurAPI) => {
  const id = parseInt(String(color.id_couleur), 10);
  if (isColorDisabled(id)) return;
  if (props.single) {
    pendingIds.value = pendingIds.value.has(id) ? new Set() : new Set([id]);
    return;
  }
  const next = new Set(pendingIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  pendingIds.value = next;
};

const emitClose = () => {
  emit("update:isVisible", false);
};

const confirmAdd = () => {
  const selected = availableColors.value.filter((color) =>
    pendingIds.value.has(parseInt(String(color.id_couleur), 10)),
  );
  emit("confirm", selected);
  emit("update:isVisible", false);
};

const resetState = () => {
  searchTerm.value = "";
  const initial = new Set<number>();
  for (const id of props.selectedIds || []) {
    initial.add(parseInt(String(id), 10));
  }
  pendingIds.value = initial;

  const colorFamilies = getFamiliesWithVendorColors(solidColorsRef.value);
  const motifCategories = getMotifCategoriesWithColors(motifColorsRef.value);

  if (colorFamilies.length) {
    activeSection.value = "couleurs";
    activeFamilyId.value = colorFamilies[0]?.id || "autres";
    couleursSectionOpen.value = true;
    motifsSectionOpen.value = false;
  } else if (motifCategories.length) {
    activeSection.value = "motifs";
    activeMotifCategory.value = motifCategories[0].id;
    couleursSectionOpen.value = false;
    motifsSectionOpen.value = true;
  }
};

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      resetState();
      nextTick(() => {
        disableBodyScroll();
        searchInputRef.value?.focus();
      });
    } else {
      enableBodyScroll();
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  enableBodyScroll();
});

watch([visibleColorFamilies, visibleMotifCategories], ([families, categories]) => {
  if (
    activeSection.value === "couleurs" &&
    !families.find((family) => family.id === activeFamilyId.value)
  ) {
    if (families[0]) {
      activeFamilyId.value = families[0].id;
    } else if (categories[0]) {
      activeSection.value = "motifs";
      activeMotifCategory.value = categories[0].id;
      motifsSectionOpen.value = true;
      couleursSectionOpen.value = false;
    }
  }

  if (
    activeSection.value === "motifs" &&
    !categories.find((category) => category.id === activeMotifCategory.value)
  ) {
    if (categories[0]) {
      activeMotifCategory.value = categories[0].id;
    } else if (families[0]) {
      activeSection.value = "couleurs";
      activeFamilyId.value = families[0].id;
      couleursSectionOpen.value = true;
      motifsSectionOpen.value = false;
    }
  }
});

watch(searchTerm, () => {
  highlightedColorIndex.value = -1;
  if (!searchTerm.value) return;

  if (visibleColorFamilies.value[0]) {
    activeSection.value = "couleurs";
    activeFamilyId.value = visibleColorFamilies.value[0].id;
    couleursSectionOpen.value = true;
    motifsSectionOpen.value = false;
    return;
  }

  if (visibleMotifCategories.value[0]) {
    activeSection.value = "motifs";
    activeMotifCategory.value = visibleMotifCategories.value[0].id;
    motifsSectionOpen.value = true;
    couleursSectionOpen.value = false;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (_ctx.isVisible)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "fixed inset-0 z-[9999] overflow-hidden overscroll-none flex items-center justify-center p-3 md:p-4",
          onWheel: _cache[7] || (_cache[7] = _withModifiers(() => {}, ["prevent"]))
        }, [
          _createElementVNode("div", {
            class: "absolute inset-0 bg-black/40 transition-opacity",
            onClick: emitClose,
            onTouchmove: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"]))
          }, null, 32),
          _createElementVNode("div", {
            ref_key: "modalRef",
            ref: modalRef,
            class: "relative w-full max-w-4xl bg-white rounded-2xl shadow-xl h-[85dvh] md:h-auto md:max-h-[90vh] overflow-hidden flex flex-col overscroll-contain",
            onWheel: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["stop"])),
            onClick: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("div", _hoisted_1, [
              _cache[11] || (_cache[11] = _createElementVNode("h3", { class: "text-base sm:text-lg font-semibold text-black whitespace-nowrap" }, " Couleurs/motifs ", -1)),
              _createElementVNode("div", _hoisted_2, [
                _cache[9] || (_cache[9] = _createElementVNode("i", { class: "fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" }, null, -1)),
                _withDirectives(_createElementVNode("input", {
                  ref_key: "searchInputRef",
                  ref: searchInputRef,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchTerm).value = $event)),
                  type: "text",
                  autocomplete: "off",
                  placeholder: "Rechercher une couleur",
                  class: "w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-black focus:border-black",
                  role: "combobox",
                  "aria-label": "Rechercher une couleur ou un motif",
                  "aria-expanded": isSearchDropdownOpen.value,
                  onFocus: openSearchDropdown,
                  onBlur: closeSearchDropdown,
                  onInput: openSearchDropdown,
                  onKeydown: [
                    _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (moveColorHighlight(1)), ["prevent"]), ["down"])),
                    _cache[3] || (_cache[3] = _withKeys(_withModifiers(($event: any) => (moveColorHighlight(-1)), ["prevent"]), ["up"])),
                    _withKeys(_withModifiers(selectHighlightedColor, ["prevent"]), ["enter"]),
                    _withKeys(_withModifiers(focusConfirmButton, ["exact","prevent"]), ["tab"])
                  ]
                }, null, 40, _hoisted_3), [
                  [
                    _vModelText,
                    searchTerm.value,
                    void 0,
                    { trim: true }
                  ]
                ]),
                (searchTerm.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      type: "button",
                      tabindex: "-1",
                      class: "absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:text-black",
                      "aria-label": "Effacer la recherche",
                      onMousedown: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent"])),
                      onClick: clearSearch
                    }, _cache[8] || (_cache[8] = [
                      _createElementVNode("i", { class: "fas fa-times text-xs" }, null, -1)
                    ]), 32))
                  : _createCommentVNode("", true),
                (isSearchDropdownOpen.value && searchTerm.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      (searchResults.value.length === 0)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_5, " Aucune couleur trouvée "))
                        : _createCommentVNode("", true),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchResults.value, (color, index) => {
                        return (_openBlock(), _createElementBlock("button", {
                          key: `search-${color.id_couleur}`,
                          type: "button",
                          tabindex: "-1",
                          "data-color-search-result": color.id_couleur,
                          class: _normalizeClass(["flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0", 
                index === highlightedColorIndex.value
                  ? 'bg-blue-100'
                  : 'hover:bg-gray-100'
              ]),
                          role: "option",
                          "aria-selected": index === highlightedColorIndex.value,
                          onMousedown: _withModifiers(($event: any) => (selectSearchResult(color)), ["prevent"])
                        }, [
                          _createElementVNode("span", {
                            class: "color-swatch-dot h-4 w-4 flex-shrink-0 rounded-full",
                            style: _normalizeStyle(_unref(getColorSwatchStyle)(color.couleur_FR, color.hexColor))
                          }, null, 4),
                          _createElementVNode("span", _hoisted_7, _toDisplayString(color.couleur_FR), 1)
                        ], 42, _hoisted_6))
                      }), 128))
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "text-gray-500 hover:text-black p-2 flex-shrink-0",
                "aria-label": "Fermer",
                onClick: emitClose
              }, _cache[10] || (_cache[10] = [
                _createElementVNode("i", { class: "fas fa-times" }, null, -1)
              ]))
            ]),
            _createElementVNode("div", _hoisted_8, [
              (visibleColorFamilies.value.length)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "flex items-center justify-between px-3 py-2 text-sm font-bold text-black uppercase tracking-wide",
                      onClick: toggleCouleursSection
                    }, [
                      _cache[12] || (_cache[12] = _createElementVNode("span", null, "Couleurs", -1)),
                      _createElementVNode("i", {
                        class: _normalizeClass(["fas fa-chevron-down collapse-chevron text-[10px] text-gray-400", { 'collapse-chevron--open': couleursSectionOpen.value }])
                      }, null, 2)
                    ]),
                    _createElementVNode("div", {
                      class: _normalizeClass(["collapse-panel", couleursSectionOpen.value ? 'collapse-panel--open' : 'collapse-panel--closed'])
                    }, [
                      _createElementVNode("div", _hoisted_9, [
                        _createElementVNode("div", _hoisted_10, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleColorFamilies.value, (family) => {
                            return (_openBlock(), _createElementBlock("button", {
                              key: 'mc-' + family.id,
                              type: "button",
                              class: _normalizeClass(["flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs whitespace-nowrap border flex-shrink-0 transition-colors", navButtonClass('couleurs', family.id)]),
                              onClick: ($event: any) => (selectColorFamily(family.id))
                            }, [
                              _createElementVNode("span", {
                                class: "color-swatch-dot w-3.5 h-3.5 rounded-full flex-shrink-0",
                                style: _normalizeStyle(familySwatchStyle(family))
                              }, null, 4),
                              _createTextVNode(" " + _toDisplayString(family.label), 1)
                            ], 10, _hoisted_11))
                          }), 128))
                        ])
                      ])
                    ], 2)
                  ], 64))
                : _createCommentVNode("", true),
              (visibleMotifCategories.value.length)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createElementVNode("button", {
                      type: "button",
                      class: _normalizeClass(["flex items-center justify-between px-3 py-2 text-sm font-bold text-black uppercase tracking-wide", visibleColorFamilies.value.length ? 'border-t border-gray-100' : '']),
                      onClick: toggleMotifsSection
                    }, [
                      _cache[13] || (_cache[13] = _createElementVNode("span", null, "Motifs", -1)),
                      _createElementVNode("i", {
                        class: _normalizeClass(["fas fa-chevron-down collapse-chevron text-[10px] text-gray-400", { 'collapse-chevron--open': motifsSectionOpen.value }])
                      }, null, 2)
                    ], 2),
                    _createElementVNode("div", {
                      class: _normalizeClass(["collapse-panel", motifsSectionOpen.value ? 'collapse-panel--open' : 'collapse-panel--closed'])
                    }, [
                      _createElementVNode("div", _hoisted_12, [
                        _createElementVNode("div", _hoisted_13, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleMotifCategories.value, (category) => {
                            return (_openBlock(), _createElementBlock("button", {
                              key: 'mm-' + category.id,
                              type: "button",
                              class: _normalizeClass(["flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs whitespace-nowrap border flex-shrink-0 transition-colors", navButtonClass('motifs', category.id)]),
                              onClick: ($event: any) => (selectMotifCategory(category.id))
                            }, _toDisplayString(category.label), 11, _hoisted_14))
                          }), 128))
                        ])
                      ])
                    ], 2)
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_15, [
              _createElementVNode("aside", _hoisted_16, [
                (visibleColorFamilies.value.length)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("button", {
                        type: "button",
                        class: "w-full flex items-center justify-between px-3 py-2.5 text-sm font-bold text-black uppercase tracking-wide hover:bg-white/70 transition-colors",
                        onClick: toggleCouleursSection
                      }, [
                        _cache[14] || (_cache[14] = _createElementVNode("span", null, "Couleurs", -1)),
                        _createElementVNode("i", {
                          class: _normalizeClass(["fas fa-chevron-down collapse-chevron text-[10px] text-gray-400", { 'collapse-chevron--open': couleursSectionOpen.value }])
                        }, null, 2)
                      ]),
                      _createElementVNode("div", {
                        class: _normalizeClass(["collapse-panel", couleursSectionOpen.value ? 'collapse-panel--open' : 'collapse-panel--closed'])
                      }, [
                        _createElementVNode("div", _hoisted_17, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleColorFamilies.value, (family) => {
                            return (_openBlock(), _createElementBlock("button", {
                              key: family.id,
                              type: "button",
                              class: _normalizeClass(["w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors", sidebarItemClass('couleurs', family.id)]),
                              onClick: ($event: any) => (selectColorFamily(family.id))
                            }, [
                              _createElementVNode("span", {
                                class: "color-swatch-dot w-5 h-5 rounded-full flex-shrink-0",
                                style: _normalizeStyle(familySwatchStyle(family))
                              }, null, 4),
                              _createElementVNode("span", _hoisted_19, _toDisplayString(family.label), 1),
                              (pendingCountByColorFamily.value[family.id])
                                ? (_openBlock(), _createElementBlock("span", _hoisted_20, " (" + _toDisplayString(pendingCountByColorFamily.value[family.id]) + ") ", 1))
                                : _createCommentVNode("", true)
                            ], 10, _hoisted_18))
                          }), 128))
                        ])
                      ], 2)
                    ], 64))
                  : _createCommentVNode("", true),
                (visibleMotifCategories.value.length)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("button", {
                        type: "button",
                        class: _normalizeClass(["w-full flex items-center justify-between px-3 py-2.5 text-sm font-bold text-black uppercase tracking-wide hover:bg-white/70 transition-colors", visibleColorFamilies.value.length ? 'border-t border-gray-200' : '']),
                        onClick: toggleMotifsSection
                      }, [
                        _cache[15] || (_cache[15] = _createElementVNode("span", null, "Motifs", -1)),
                        _createElementVNode("i", {
                          class: _normalizeClass(["fas fa-chevron-down collapse-chevron text-[10px] text-gray-400", { 'collapse-chevron--open': motifsSectionOpen.value }])
                        }, null, 2)
                      ], 2),
                      _createElementVNode("div", {
                        class: _normalizeClass(["collapse-panel", motifsSectionOpen.value ? 'collapse-panel--open' : 'collapse-panel--closed'])
                      }, [
                        _createElementVNode("div", _hoisted_21, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleMotifCategories.value, (category) => {
                            return (_openBlock(), _createElementBlock("button", {
                              key: category.id,
                              type: "button",
                              class: _normalizeClass(["w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors", sidebarItemClass('motifs', category.id)]),
                              onClick: ($event: any) => (selectMotifCategory(category.id))
                            }, [
                              _createElementVNode("span", _hoisted_23, _toDisplayString(category.label), 1),
                              (pendingCountByMotifCategory.value[category.id])
                                ? (_openBlock(), _createElementBlock("span", _hoisted_24, " (" + _toDisplayString(pendingCountByMotifCategory.value[category.id]) + ") ", 1))
                                : _createCommentVNode("", true)
                            ], 10, _hoisted_22))
                          }), 128))
                        ])
                      ], 2)
                    ], 64))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("section", _hoisted_25, [
                (panelTitle.value)
                  ? (_openBlock(), _createElementBlock("h4", _hoisted_26, _toDisplayString(panelTitle.value), 1))
                  : _createCommentVNode("", true),
                (displayedShades.value.length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_27, _toDisplayString(searchTerm.value
                ? "Aucune couleur trouvée"
                : activeSection.value === "motifs"
                  ? "Aucun motif dans cette famille"
                  : "Aucune couleur dans cette famille"), 1))
                  : (_openBlock(), _createElementBlock("div", _hoisted_28, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayedShades.value, (color) => {
                        return (_openBlock(), _createElementBlock("button", {
                          key: color.id_couleur,
                          type: "button",
                          tabindex: "-1",
                          class: _normalizeClass(["group text-left focus:outline-none", [
                isColorDisabled(color.id_couleur) ? 'opacity-40 cursor-not-allowed' : '',
                isMotif(color)
                  ? isPending(color.id_couleur)
                    ? 'rounded-md border border-black ring-1 ring-black px-2.5 py-2'
                    : 'rounded-md border border-gray-200 px-2.5 py-2 hover:border-gray-400'
                  : '',
              ]]),
                          disabled: isColorDisabled(color.id_couleur),
                          onClick: ($event: any) => (toggleColor(color))
                        }, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["text-xs sm:text-sm text-black truncate flex items-center justify-between gap-2", isMotif(color) ? '' : 'mb-1.5'])
                          }, [
                            _createElementVNode("span", _hoisted_30, _toDisplayString(color.couleur_FR), 1),
                            (isMotif(color) && isPending(color.id_couleur))
                              ? (_openBlock(), _createElementBlock("i", _hoisted_31))
                              : _createCommentVNode("", true)
                          ], 2),
                          (!isMotif(color))
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass(["relative w-full h-12 sm:h-16 rounded-md border overflow-hidden transition-all", 
                  isPending(color.id_couleur)
                    ? 'border-black ring-1 ring-black'
                    : 'border-gray-200 group-hover:border-gray-400'
                ]),
                                style: _normalizeStyle(_unref(getColorSwatchStyle)(color.couleur_FR, color.hexColor))
                              }, [
                                (isPending(color.id_couleur))
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_32, _cache[16] || (_cache[16] = [
                                      _createElementVNode("i", { class: "fas fa-check text-[8px]" }, null, -1)
                                    ])))
                                  : _createCommentVNode("", true)
                              ], 6))
                            : _createCommentVNode("", true)
                        ], 10, _hoisted_29))
                      }), 128))
                    ]))
              ])
            ]),
            _createElementVNode("div", _hoisted_33, [
              _createElementVNode("button", {
                type: "button",
                class: "px-4 py-2 text-sm border border-gray-800 rounded-lg hover:bg-gray-50 transition-colors",
                onClick: emitClose
              }, " Annuler "),
              _createElementVNode("button", {
                ref_key: "confirmButtonRef",
                ref: confirmButtonRef,
                type: "button",
                class: "px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800 transition-colors",
                onClick: confirmAdd,
                onKeydown: _withKeys(_withModifiers(focusSearchInput, ["exact","prevent"]), ["tab"])
              }, _toDisplayString(addButtonLabel.value), 41, _hoisted_34)
            ])
          ], 544)
        ], 32))
      : _createCommentVNode("", true)
  ]))
}
}

})